import { translateFn } from '../../../utils/translator.helper';
import { AccountConstraints } from '../constraints/AccountConstraints';

export function getAccountsStrings(): object {
  return {
    noResultsMessage: translateFn('Start adding accounts'),
    noResultsAdditionalText: translateFn("You don't have any accounts here yet"),
    noADUsers: translateFn('No AD users'),
    addAccountButtonText: translateFn('Add account'),
    deleteAccountButtonText: translateFn('Delete'),
    detailsTitle: translateFn('Details'),
    importFromActiveDirectoryLabel: translateFn('Import from Active Directory'),
    importFromActiveDirectoryDescription: translateFn('Click the Synchronize button if you cannot find the user.'),
    activeDirectorySynchronizeButton: translateFn('Synchronize'),

    emailLabel: translateFn('Email'),
    emailMaxLength: translateFn('Email must not be longer than {emailMaxLength} characters in length.', {
      emailMaxLength: AccountConstraints.Email.MaxLength,
    }),
    invalidEmail: translateFn('Please provide a valid email address.'),
    emailAlreadyExists: translateFn('This email already exists.'),

    fullNameLabel: translateFn('Full name'),
    fullNameMaxLength: translateFn('Full name must not be longer than {fullNameMaxLength} characters in length.', {
      fullNameMaxLength: AccountConstraints.FullName.MaxLength,
    }),
    fullNameInvalidCharacters: translateFn('Your full name contains characters that are not allowed.'),

    usernameLabel: translateFn('Username'),
    usernameAlreadyExists: translateFn('Username already exists.'),
    userNameMaxLength: translateFn('Username must not be longer than {userNameMaxLength} characters.', {
      userNameMaxLength: AccountConstraints.Username.MaxLength,
    }),
    userNameMinLength: translateFn('Username must be at least {userNameMinLength} characters in length.', {
      userNameMinLength: AccountConstraints.Username.MinLength,
    }),
    invalidUserName: translateFn('Username contains characters that are not allowed.'),

    passwordLabel: translateFn('Password'),
    confirmPasswordLabel: translateFn('Confirm password'),
    confirmPasswordDescription: translateFn(
      'The password must meet the minimum complexity requirements: 12 characters length, one digit, one upper case, one lower case and one special character.'
    ),
    passwordsDoNotMatch: translateFn('Passwords do not match.'),
    passwordMinLength: translateFn('Password must be at least {passwordMinLength} characters in length.', {
      passwordMinLength: AccountConstraints.Password.MinLength,
    }),
    passwordMaxLength: translateFn('Password must not be longer than {passwordMaxLength} characters in length.', {
      passwordMaxLength: AccountConstraints.Password.MaxLength,
    }),
    invalidPassword: translateFn(
      'The password must be at least {passwordMinLength} characters in length, and must contain one digit, one upper case, one lower case and one special character.',
      {
        passwordMinLength: AccountConstraints.Password.MinLength,
      }
    ),
    emptyPhoneNumberField: translateFn('The phone number field cannot be empty.'),
    emptyCountryCodeField: translateFn('The country code field cannot be empty.'),
    invalidPhoneNumber: translateFn(
      'The specified phone number is not valid. The input value must be between 3 and 14 characters.'
    ),
    phoneNumberMaxLength: translateFn('The phone number must have maximum 15 characters.'),
    noCountryCodeOptionsFound: translateFn('No options found'),
    passwordUsedInThePast: translateFn('The new password needs to be different from previously used passwords.'),

    companySelectorLabel: translateFn('Company'),
    timezoneLabel: translateFn('Timezone'),
    languageLabel: translateFn('Language'),
    phoneNumberLabel: translateFn('Phone number'),
    countrySelectorLabel: translateFn('Country'),
    loginSecurityTitle: translateFn('Login Security'),
    authenticationMethodLabel: translateFn('Authentication method'),
    passwordExpirationCheckboxLabel: translateFn('Set maximum password age to 90 days'),
    passwordExpirationDescription: translateFn(
      'This option enables the password expiration policy. The user needs to change their passwords sooner than the specified age. Otherwise, they will not be able to log into GravityZone anymore.'
    ),
    accountLockoutCheckboxLabel: translateFn('Lock out account after 5 login attempts with invalid passwords'),
    accountLockoutDescription: translateFn(
      'This option limits the number of consecutive invalid passwords to prevent attacks. When the counter reaches the maximum number of allowed attempts, the account is locked out and the user needs to reset their password.'
    ),
    concurrentSessionsCheckboxLabel: translateFn('Concurrent sessions'),
    concurrentSessionsDescription: translateFn('If enabled, the user will be able to log in to GravityZone from multiple browsers, keeping all sessions active. If disabled, starting a new session will automatically close any existing ones.'),
    rolePrivilegesTitle: translateFn('Role Privileges'),
    noRightsSelectedErrorMessage: translateFn('You need to select at least one right for the user.'),
    roleLabel: translateFn('Role'),
    rightsLabel: translateFn('Rights *'),
    manageCompaniesRightCheckboxLabel: translateFn('Manage Companies'),
    manageCompaniesRightDescription: translateFn(
      'Create, edit, suspend, delete company accounts and manage their subscriptions.'
    ),
    manageUsersRightCheckboxLabel: translateFn('Manage Users'),
    manageUsersRightDescription: translateFn('Create, edit, and delete user accounts.'),
    manageCompanyRightCheckboxLabel: translateFn('Manage Company'),
    manageCompanyRightDescription: translateFn("Manage your company's subscription and edit its profile."),
    manageInventoryRightCheckboxLabel: translateFn('Manage Networks'),
    manageInventoryRightDescription: translateFn(
      'Create and download installation packages; install clients; manage tasks and quarantined files.'
    ),
    manageReportsRightCheckboxLabel: translateFn('View and Analyze Data'),
    manageReportsRightDescription: translateFn('Create, edit and delete reports.'),
    manageSolutionRightCheckboxLabel: translateFn('Manage Solution'),
    manageSolutionRightDescription: translateFn(
      'Configure GravityZone Control Center settings (such as integration with AD and virtualization platforms, security certificates and GravityZone updates).'
    ),
    managePoliciesRightCheckboxLabel: translateFn('Manage Endpoint Settings'),
    managePoliciesRightDescription: translateFn(
      'View or manage policies, configuration profiles, assignment rules and any other endpoint from other GravityZone areas.'
    ),
    managePoliciesReadRightCheckboxLabel: translateFn('Read only'),
    managePoliciesReadRightDescription: translateFn('View endpoint settings.'),
    managePoliciesWriteRightCheckboxLabel: translateFn('Read and Write'),
    managePoliciesWriteRightDescription: translateFn(
      'Create, edit and delete company policies, configuration profiles, manage endpoint settings from other GravityZone areas.'
    ),
    advancedInvestigationRightCheckboxLabel: translateFn('Advanced Investigation'),
    advancedInvestigationRightDescription: translateFn('Enable access to advanced investigation features.'),
    communicationError: translateFn('A communication error has occurred. Please try again later.'),
    checkStatusOfThePoliciesTwoFAString: translateFn(
      'Check the status of the policies available to secure your GravityZone account.'
    ),
    readOnlyString: translateFn('Policies set company-wide are read-only.'),
    twoFAEnabledStatusString: translateFn('Two-factor authenticator is {enabledBold} for this user account.', {
      enabledBold: '<b>' + translateFn('enabled') + '</b>',
    }),
    twoFADisabledStatusString: translateFn('Two-factor authenticator is {disabledBold} for this user account.', {
      disabledBold: '<b>' + translateFn('disabled') + '</b>',
    }),
    twoFAInfoString: translateFn(
      'Two-factor authentication can be enabled for a user account only by its owner, from My Account page. You need to ask the user to activate this feature as follows:'
    ),
    firstStepToEnable2faString: translateFn(
      'Point to the user menu in the upper-right corner of the console and click My Account.'
    ),
    secondStepToEnable2faString: translateFn('Under Login Security, click Enable.'),
    thirdStepToEnable2faString: translateFn(
      'Download and configure the authenticator app on a mobile device or computer.'
    ),
    fourthStepToEnable2faString: translateFn('Click Enable.'),
    reset2faString: translateFn(
      'To reset two-factor authentication for this user account, enter your GravityZone password in the field below.'
    ),
    gravityZonePasswordLabel: translateFn('Your GravityZone password'),
    revokeResetDescriptionString: translateFn(
      'Revoke trusted browsers that skip 2FA or completely reset 2FA for this account:'
    ),
    revokeButtonLabel: translateFn('Revoke all browsers'),
    resetButtonLabel: translateFn('Reset 2FA'),
    afterResetString: translateFn(
      'After reset, the current user will be prompted to configure again the two-factor authentication with a new secret key.'
    ),
    unlinkConfirmationString: translateFn(
      'Are you sure you want to unlink this user? The edited user will be unlinked from its associated access permission rule.'
    ),
    unlinkConfirmationButtonString: translateFn('Unlink'),
    cancelButtonLabel: translateFn('Cancel'),
    unlinkConfirmationWindowTitle: translateFn('Unlink user'),
    passwordPlaceHolder: translateFn('Click here to change your password'),
    confirmPasswordPlaceholder: translateFn('Click here to retype your password'),
  };
}
